import { z } from 'zod';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { TIME_HH_MM_REGEX } from '@/utils/timeConversion';

// ── Break section ────────────────────────────────────────────────────────

const scheduleBreakBaseSchema = z.object({
  hasBreakTime: z.boolean(),
  breakMinutes: z.coerce
    .number({ invalid_type_error: 'schedule:form.validation.breakMinutesRequired' })
    .int()
    .min(0, 'schedule:form.validation.breakMinutesMin'),
  breakStartTime: z
    .string()
    .min(1, 'schedule:form.validation.breakStartTimeRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakStartTimeFormat'),
  breakEndTime: z
    .string()
    .min(1, 'schedule:form.validation.breakEndTimeRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakEndTimeFormat'),
  breakEntryThresholdStart: z
    .string()
    .min(1, 'schedule:form.validation.breakEntryThresholdStartRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakEntryThresholdStartFormat'),
  breakEntryThresholdEnd: z
    .string()
    .min(1, 'schedule:form.validation.breakEntryThresholdEndRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakEntryThresholdEndFormat'),
  breakExitThresholdStart: z
    .string()
    .min(1, 'schedule:form.validation.breakExitThresholdStartRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakExitThresholdStartFormat'),
  breakExitThresholdEnd: z
    .string()
    .min(1, 'schedule:form.validation.breakExitThresholdEndRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.breakExitThresholdEndFormat'),
  discountBreakDelay: z.boolean(),
  hasBreakMarkings: z.boolean(),
});


// ── General section ────────────────────────────────────────────────────────

const scheduleGeneralBaseSchema = z.object({
  name: z.string().min(1, 'schedule:form.validation.nameRequired'),
  isTwoDaySchedule: z.boolean({
    required_error: 'schedule:form.validation.isTwoDayScheduleRequired',
    invalid_type_error: 'schedule:form.validation.isTwoDayScheduleRequired',
  }),
  timeZoneId: z.coerce
    .number({
      invalid_type_error: 'schedule:form.validation.timeZoneRequired',
    })
    .int()
    .positive('schedule:form.validation.timeZoneRequired'),
  shiftGroupId: z.coerce
    .number({
      invalid_type_error: 'schedule:form.validation.shiftGroupRequired',
    })
    .int()
    .positive('schedule:form.validation.shiftGroupRequired'),
});

// ── Workday section ──────────────────────────────────────────────────────

const scheduleWorkdayBaseSchema = z.object({
  // Inicio de jornada
  startTime: z
    .string()
    .min(1, 'schedule:form.validation.startTimeRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.startTimeFormat'),
  entryThresholdStart: z
    .string()
    .min(1, 'schedule:form.validation.entryThresholdStartRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.entryThresholdStartFormat'),
  entryThresholdEnd: z
    .string()
    .min(1, 'schedule:form.validation.entryThresholdEndRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.entryThresholdEndFormat'),
  entryOvertimeTolerance: z.coerce
    .number({ invalid_type_error: 'schedule:form.validation.entryOvertimeToleranceRequired' })
    .int()
    .min(0, 'schedule:form.validation.entryOvertimeToleranceMin'),
  entryDelayTolerance: z.coerce
    .number({ invalid_type_error: 'schedule:form.validation.entryDelayToleranceRequired' })
    .int()
    .min(0, 'schedule:form.validation.entryDelayToleranceMin'),
  // Fin de jornada
  endTime: z
    .string()
    .min(1, 'schedule:form.validation.endTimeRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.endTimeFormat'),
  exitThresholdStart: z
    .string()
    .min(1, 'schedule:form.validation.exitThresholdStartRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.exitThresholdStartFormat'),
  exitThresholdEnd: z
    .string()
    .min(1, 'schedule:form.validation.exitThresholdEndRequired')
    .regex(TIME_HH_MM_REGEX, 'schedule:form.validation.exitThresholdEndFormat'),
  exitOvertimeTolerance: z.coerce
    .number({ invalid_type_error: 'schedule:form.validation.exitOvertimeToleranceRequired' })
    .int()
    .min(0, 'schedule:form.validation.exitOvertimeToleranceMin'),
  earlyExitTolerance: z.coerce
    .number({ invalid_type_error: 'schedule:form.validation.earlyExitToleranceRequired' })
    .int()
    .min(0, 'schedule:form.validation.earlyExitToleranceMin'),
});

// ── Unified schema (General + Workday + Break) ───────────────────────────

export const scheduleFormSchema = scheduleGeneralBaseSchema
  .merge(scheduleWorkdayBaseSchema)
  .merge(scheduleBreakBaseSchema)
  .superRefine((data, ctx) => {
    // In minutes mode breakMinutes must be at least 1
    if (data.hasBreakTime && data.breakMinutes < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'schedule:form.validation.breakMinutesRequired',
        path: ['breakMinutes'],
      });
    }
  });


export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

// ── Form subsets (typed slices passed as props) ────────────────────────────

export interface ScheduleFormSubset {
  register: UseFormRegister<ScheduleFormValues>;
  setValue: UseFormSetValue<ScheduleFormValues>;
  watch: UseFormWatch<ScheduleFormValues>;
  errors: FieldErrors<ScheduleFormValues>;
}

export type ScheduleWorkdayFormSubset = ScheduleFormSubset;

export interface ScheduleBreakFormSubset extends ScheduleFormSubset {
  onBreakTimeToggle: (checked: boolean) => void;
}

